import { StripeCurrency } from '@/types/enums';

const ZERO_DECIMAL_CURRENCIES = new Set<StripeCurrency>([
  StripeCurrency.JPY,
  StripeCurrency.KRW,
  StripeCurrency.CLP
]);

export function getCurrencyExponent(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase() as StripeCurrency) ? 0 : 2;
}

/**
 * Format a major-unit USD amount (e.g. `0.0059`) as a currency string. Defaults to 2 decimal places;
 * callers needing finer precision — e.g. AI/OpenRouter generation costs reported at sub-cent
 * precision — can pass more. Null/undefined renders as an em dash.
 */
export function formatUsd(value: number | null | undefined, fractionDigits: number = 2): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

export function getCurrencyMultiplier(currency: string): number {
  return 10 ** getCurrencyExponent(currency);
}

export function parseMajorUnitsToMinorUnits(
  raw: string,
  currency: string = StripeCurrency.USD
): number | null {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return null;
  }

  let processing = trimmed.replace(/\s+/g, '');

  const lastDot = processing.lastIndexOf('.');
  const lastComma = processing.lastIndexOf(',');

  if (lastDot > -1 && lastComma > -1) {
    if (lastComma > lastDot) {
      processing = processing.replace(/\./g, '').replace(',', '.');
    } else {
      processing = processing.replace(/,/g, '');
    }
  } else if (lastComma > -1) {
    processing = processing.replace(/,/g, '.');
  }

  const asNumber = Number(processing);

  if (Number.isNaN(asNumber)) {
    return null;
  }

  const multiplier = getCurrencyMultiplier(currency);
  return Math.round(asNumber * multiplier);
}

export function formatMinorUnitsToDisplay(
  cents: number | null,
  currency: string = StripeCurrency.USD
): string {
  if (cents === null) {
    return '';
  }

  const exponent = getCurrencyExponent(currency);

  if (exponent === 0) {
    const str = String(cents);
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const multiplier = getCurrencyMultiplier(currency);
  const major = (cents / multiplier).toFixed(exponent);
  const parts = major.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

export function toMajorUnits(minorUnits: number, currency: string): number {
  return minorUnits / getCurrencyMultiplier(currency);
}

export function isMoneyValue(value: unknown): value is Diagonal.Core.Data.MoneyData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'amount' in value &&
    'currency' in value &&
    typeof (value as Diagonal.Core.Data.MoneyData).amount === 'number' &&
    typeof (value as Diagonal.Core.Data.MoneyData).currency === 'string'
  );
}
